import { http } from "@/lib/http";

import type {
  Alarm,
  AlarmActionCreateRequest,
  AlarmAction,
  AlarmActionList,
  AlarmCreateRequest,
  AlarmUpdateRequest,
  PaginatedAlarmHistory,
  PaginatedAlarms,
} from "./types";

export interface AlarmListFilters {
  /** Case-insensitive substring match on the alarm name. */
  name?: string;
  /** Case-insensitive exact state match (e.g. "alarm"). */
  status?: string;
}

export async function listAlarms(params: {
  page: number;
  pageSize: number;
  filters?: AlarmListFilters;
}): Promise<PaginatedAlarms> {
  const { data } = await http.get<PaginatedAlarms>("/alarms", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      // Filtered server-side before pagination; unknown params are ignored
      // until the backend support lands, so this degrades gracefully.
      name: params.filters?.name || undefined,
      status: params.filters?.status || undefined,
    },
  });
  return data;
}

export async function listAlarmHistory(params: {
  alarmId: string;
  page: number;
  pageSize: number;
}): Promise<PaginatedAlarmHistory> {
  const { data } = await http.get<PaginatedAlarmHistory>(
    `/alarms/${params.alarmId}/history`,
    { params: { page: params.page, page_size: params.pageSize } },
  );
  return data;
}

export async function getAlarm(alarmId: string): Promise<Alarm> {
  const { data } = await http.get<Alarm>(`/alarms/${alarmId}`);
  return data;
}

export async function createAlarm(
  payload: AlarmCreateRequest,
): Promise<Alarm> {
  const { data } = await http.post<Alarm>("/alarms", payload);
  return data;
}

export async function updateAlarm(
  alarmId: string,
  payload: AlarmUpdateRequest,
): Promise<Alarm> {
  const { data } = await http.put<Alarm>(`/alarms/${alarmId}`, payload);
  return data;
}

export async function deleteAlarm(alarmId: string): Promise<void> {
  await http.delete(`/alarms/${alarmId}`);
}

export async function listAlarmActions(alarmId: string): Promise<AlarmActionList> {
  const { data } = await http.get<AlarmActionList>(`/alarms/${alarmId}/actions`);
  return data;
}

export async function createAlarmAction(
  alarmId: string,
  payload: AlarmActionCreateRequest,
): Promise<AlarmAction> {
  const { data } = await http.post<AlarmAction>(
    `/alarms/${alarmId}/actions`,
    payload,
  );
  return data;
}

export async function deleteAlarmAction(
  alarmId: string,
  actionId: string,
): Promise<void> {
  await http.delete(`/alarms/${alarmId}/actions/${actionId}`);
}
